import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, Divider } from '@mui/material';
import { ArrowBack, AttachMoney, TrendingUp, Warning, CheckCircle } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import budgetsService, { Budget } from '../services/budgetsService';

const formatCurrency = (val?: number) => typeof val === 'number' ? `$${val.toLocaleString()}` : '-';
const formatDate = (val?: string) => val ? new Date(val).toLocaleString() : '-';

const statusChip = (status: Budget['status']) => {
  switch (status) {
    case 'over-budget':
      return <Chip icon={<Warning />} label="Over Budget" color="error" size="small" />;
    case 'under-budget':
      return <Chip icon={<TrendingUp />} label="Under Budget" color="success" size="small" />;
    case 'at-risk':
      return <Chip icon={<Warning />} label="At Risk" color="warning" size="small" />;
    default:
      return <Chip icon={<CheckCircle />} label="On Track" color="primary" size="small" />;
  }
};

const BudgetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [budget, setBudget] = useState<Budget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await budgetsService.getBudgetById(id!);
        if (mounted) setBudget(data as any);
      } catch (err) {
        console.error('Error loading budget:', err);
        if (mounted) setError('Failed to load budget details.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (id) load();
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading budget...</Typography>
      </Box>
    );
  }

  if (error || !budget) {
    return (
      <Box sx={{ p: 3 }}>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/budgets')} sx={{ mb: 2 }}>
          Back to Budgets
        </Button>
        <Typography color="error">{error || 'Budget not found.'}</Typography>
      </Box>
    );
  }

  const varianceColor = budget.variance > 0 ? 'success.main' : budget.variance < 0 ? 'error.main' : 'text.primary';

  return (
    <Box sx={{ p: 3 }}>
      <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/budgets')} sx={{ mb: 2 }}>
        Back to Budgets
      </Button>

      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {budget.name}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Project: {budget.project || 'Unassigned'}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="body2" color="text.secondary">Budgeted Amount</Typography>
              <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                <AttachMoney /> {formatCurrency(budget.budgetedAmount)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="body2" color="text.secondary">Actual Amount</Typography>
              <Typography variant="h6">{formatCurrency(budget.actualAmount)}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="body2" color="text.secondary">Committed Amount</Typography>
              <Typography variant="h6">{formatCurrency(budget.committedAmount)}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="body2" color="text.secondary">Variance</Typography>
              <Typography variant="h6" sx={{ color: varianceColor }}>{formatCurrency(budget.variance)}</Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            {statusChip(budget.status)}
            <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
            <Typography variant="body2">{formatDate(budget.lastUpdated)}</Typography>
            <Typography variant="body2" color="text.secondary">Created By:</Typography>
            <Typography variant="body2">{budget.createdBy || 'System'}</Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BudgetDetail;